import { call, put, takeEvery, select } from 'redux-saga/effects';
import * as actNotif from 'actions/notifications';
import { outlets as getOutlets } from 'api/outlets';
import getConfigurationListSelector from 'selectors/getConfigurationList';
import isFeatureEnabledSelector from 'selectors/isFeatureEnabled';
import { useMokaOutletPropsFirst } from 'utils/formatter';
import {
  CONFIGURATIONS_FETCH,
  CONFIGURATION_DETAIL_FETCH,
  PAYMENT_METHODS_FETCH,
  CREATE_CONFIGURATION,
  REMOVE_CONFIGURATION,
  CONFIGURATION_UPDATE,
  PAYMENT_METHOD_CATEGORIES_FETCH,
  OUTLET_FETCH,
} from './types';
import {
  fetchConfigurations,
  fetchConfigurationsFailure,
  fetchConfigurationsSuccess,
  fetchConfigurationDetailFailure,
  fetchConfigurationDetailSuccess,
  fetchPaymentMethodsFailure,
  fetchPaymentMethodsSuccess,
  fetchPaymentMethodCategoriesFailure,
  fetchPaymentMethodCategoriesSuccess,
  createConfigurationFailure,
  createConfigurationSuccess,
  removeConfigurationSuccess,
  removeConfigurationFailure,
  updateConfigurationSuccess,
  updateConfigurationFailure,
  fetchOutletsFailure,
  fetchOutletsSuccess,
} from './actions';
import {
  getConfigurationList,
  getConfigurationDetail,
  getAllConfigurablePaymentMethods,
  getAllPaymentMethodCategories,
  postNewConfiguration,
  deleteConfiguration,
  updateConfiguration,
  getOutletConfigurationLinks,
} from './api';
import { getMockOutlets } from './fakeServer';

function* outletsFetchSaga() {
  try {
    const useRealApi = yield select(
      isFeatureEnabledSelector,
      'use-payment-configuration-api'
    );
    let outlets;

    if (useRealApi) {
      const { data } = yield call(getOutlets);
      outlets = data;
    } else {
      outlets = yield call(getMockOutlets);
    }

    const outletLinks = yield call(getOutletConfigurationLinks);
    const configurations = yield select(getConfigurationListSelector);
    const configurationMap = new Map();
    for (const config of configurations) {
      configurationMap.set(config.id, config);
    }

    const result = outlets
      .map(outlet => {
        const { id } = outlet;
        const configuration = configurationMap.get(outletLinks[id]);

        return {
          id,
          name: useMokaOutletPropsFirst(outlet, o => o.name),
          paymentConfiguration: {
            id: configuration.id,
            name: configuration.name,
          },
        };
      })
      .sort((a, b) => {
        const configA = configurationMap.get(outletLinks[a.id]);
        const configB = configurationMap.get(outletLinks[b.id]);

        if (configA.isDefaultConfiguration) {
          return -1;
        }
        if (configB.isDefaultConfiguration) {
          return 1;
        }

        return configA.name.localeCompare(configB.name);
      });

    yield put(fetchOutletsSuccess(result));
  } catch (e) {
    yield put(fetchOutletsFailure(e));
  }
}

function* configurationsFetchSaga() {
  try {
    const configurations = yield call(getConfigurationList);
    yield put(fetchConfigurationsSuccess(configurations));
  } catch (e) {
    yield put(fetchConfigurationsFailure(e));
  }
}

function* configurationDetailFetchSaga(action) {
  try {
    const { configurationId } = action.payload;
    const detail = yield call(getConfigurationDetail, configurationId);
    yield put(fetchConfigurationDetailSuccess(detail));
  } catch (e) {
    yield put(fetchConfigurationDetailFailure(e));
  }
}

function* paymentMethodsFetchSaga() {
  try {
    const getPaymentMethods = yield call(getAllConfigurablePaymentMethods);

    const paymentMethods = getPaymentMethods
      .filter(method => method.isActive)
      .sort((a, b) => {
        return a.displayOrder - b.displayOrder;
      });

    yield put(fetchPaymentMethodsSuccess(paymentMethods));
  } catch (e) {
    yield put(fetchPaymentMethodsFailure(e));
  }
}

function* createConfigurationSaga(action) {
  try {
    const { newConfiguration } = action.payload;
    const result = yield call(postNewConfiguration, {
      outletIds: [],
      paymentMethodIds: [],
      ...newConfiguration,
    });
    yield put(createConfigurationSuccess(result));
    yield put(actNotif.success('Configuration successfully added'));
    yield put(fetchConfigurations());
  } catch (e) {
    yield put(createConfigurationFailure(e));
  }
}

function* updateConfigurationSaga(action) {
  try {
    const { configurationId, patchBody } = action.payload;
    const result = yield call(updateConfiguration, configurationId, patchBody);
    yield put(updateConfigurationSuccess(result));
    yield put(actNotif.success('Configuration successfully updated'));
    yield put(fetchConfigurations());
  } catch (e) {
    yield put(updateConfigurationFailure(e));
  }
}

function* removeConfigurationSaga(action) {
  try {
    const { configurationId } = action.payload;
    const result = yield call(deleteConfiguration, configurationId);
    yield put(removeConfigurationSuccess(result));
    yield put(actNotif.success('Configuration successfully removed'));
    yield put(fetchConfigurations());
  } catch (e) {
    yield put(removeConfigurationFailure(e));
  }
}

function* paymentMethodCategoriesFetchSaga() {
  try {
    const result = yield call(getAllPaymentMethodCategories);
    yield put(fetchPaymentMethodCategoriesSuccess(result));
  } catch (e) {
    yield put(fetchPaymentMethodCategoriesFailure(e));
  }
}

export default function* saga() {
  yield takeEvery(OUTLET_FETCH, outletsFetchSaga);
  yield takeEvery(CONFIGURATIONS_FETCH, configurationsFetchSaga);
  yield takeEvery(CONFIGURATION_DETAIL_FETCH, configurationDetailFetchSaga);
  yield takeEvery(PAYMENT_METHODS_FETCH, paymentMethodsFetchSaga);
  yield takeEvery(CREATE_CONFIGURATION, createConfigurationSaga);
  yield takeEvery(CONFIGURATION_UPDATE, updateConfigurationSaga);
  yield takeEvery(REMOVE_CONFIGURATION, removeConfigurationSaga);
  yield takeEvery(
    PAYMENT_METHOD_CATEGORIES_FETCH,
    paymentMethodCategoriesFetchSaga
  );
}
